import { useMemo, useState } from 'react';
import styles from './ChiroToolPicker.module.css';
import type { ChiroTool } from '../types';
import { BODY_PARTS } from '../types';
import { hasHandler } from '../handlers';
import { guessBodyPart } from '../bodyPartGuess';
import type { PluginStudyContext } from '../../types';
import tools from '../data/tools.json';

// Vite import.meta.glob for the asset thumbnails — resolves to a map of
// filename → URL string at build time, so we don't have to write a 59-line
// import list.
const thumbUrls = import.meta.glob<string>('../assets/*.jpg', {
  eager: true,
  import: 'default',
  query: '?url',
});

function thumbUrl(toolId: number): string | undefined {
  const key = `../assets/${toolId}.jpg`;
  return thumbUrls[key];
}

interface Props {
  onSelectTool: (toolId: number) => void;
  onClose: () => void;
  /** Study metadata from the plugin context — used to preselect the
   *  matching region tab so users of a lumbar-spine study don't land on
   *  the Skull tab. */
  studyContext?: PluginStudyContext | null;
}

export default function ChiroToolPicker({ onSelectTool, onClose, studyContext }: Props) {
  const guessedRegion = useMemo(() => guessBodyPart(studyContext ?? null), [studyContext]);
  const [region, setRegion] = useState<number>(guessedRegion ?? BODY_PARTS[0].id);

  const regionTools = useMemo(
    () => (tools as ChiroTool[]).filter((t) => t.visible && t.bodyPart === region),
    [region],
  );

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Chiropractic Measurement Tools</h2>
            {guessedRegion !== null && (
              <div className={styles.subhint}>
                {`Auto-selected ${BODY_PARTS.find((b) => b.id === guessedRegion)?.label} from `}
                <span style={{ color: '#ddd' }}>
                  {studyContext?.bodyPart
                    || studyContext?.seriesDescription
                    || studyContext?.studyDescription
                    || 'study'}
                </span>
                . Switch tabs to browse other regions.
              </div>
            )}
          </div>
          <button className={styles.close} onClick={onClose} title="Close">×</button>
        </div>
        <div className={styles.body}>
          <div className={styles.regions}>
            {BODY_PARTS.map((bp) => (
              <button
                key={bp.id}
                className={`${styles.regionBtn} ${region === bp.id ? styles.regionActive : ''}`}
                onClick={() => setRegion(bp.id)}
              >
                {bp.label}
              </button>
            ))}
          </div>
          <div className={styles.grid}>
            {regionTools.map((t) => {
              const available = hasHandler(t.id);
              return (
                <div
                  key={t.id}
                  className={`${styles.card} ${!available ? styles.disabled : ''}`}
                  onClick={() => available && onSelectTool(t.id)}
                  title={available ? t.name : `${t.name} (not yet implemented)`}
                >
                  {thumbUrl(t.id) && (
                    <img className={styles.thumb} src={thumbUrl(t.id)} alt={t.name} />
                  )}
                  <div className={styles.cardName}>{t.name}</div>
                  <span className={`${styles.badge} ${available ? styles.badgeAvailable : ''}`}>
                    {available ? 'Ready' : 'Soon'}
                  </span>
                </div>
              );
            })}
            {regionTools.length === 0 && (
              <div style={{ color: '#888', fontSize: 13 }}>No tools in this region.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
